/**
 * „Clienți" din raportul financiar: cine a facturat, cine a plătit, cine
 * întârzie, în câte zile se încasează de obicei și cât de concentrate sunt
 * veniturile. Clienții noi și cei adormiți sunt scoși separat, fiindcă sunt
 * exact cele două liste pe care se acționează.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ClientsSection as ClientsData } from '../../../lib/monthlyReportApi';
import { REPORT_CHART_COLORS, fmtDate, fmtMoney, fmtPct } from '../../../lib/monthlyReportFormat';
import { bucketLabel, type ReportPeriodKind } from '../../../lib/reportPeriod';
import { CHART_TOOLTIP_LABEL_STYLE, CHART_TOOLTIP_STYLE } from '../../../lib/chartTheme';
import { useI18n } from '../../../contexts/I18nContext';
import { Empty, PartyList, ReportSection, Stat } from './ReportSections';

const ROWS_PREVIEW = 8;
const AXIS = { fill: '#6B7280', fontSize: 11 };
const GRID = '#E5E7EB';

function compact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

export function ClientsSection({
  clients,
  bucketKind = 'month',
}: {
  clients: ClientsData;
  bucketKind?: ReportPeriodKind;
}) {
  const { t, locale } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? clients.rows : clients.rows.slice(0, ROWS_PREVIEW);
  const trend = clients.trend.map((p) => ({
    label: bucketLabel(p.period, bucketKind, locale),
    invoiced: p.invoiced,
    collected: p.collected,
  }));

  return (
    <ReportSection title={t('monthlyReport.section.clients')}>
      {clients.rows.length === 0 ? (
        <Empty text={t('monthlyReport.clients.empty')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
            <Stat
              label={t('monthlyReport.clients.invoiced')}
              value={fmtMoney(clients.invoicedTotal, 'RON', locale)}
            />
            <Stat
              label={t('monthlyReport.clients.collected')}
              value={fmtMoney(clients.collectedTotal, 'RON', locale)}
            />
            <Stat
              label={t('monthlyReport.clients.dso')}
              value={
                clients.dso === null ? '—' : t('monthlyReport.clients.dsoValue', { n: clients.dso })
              }
              hint={
                clients.dsoPrevious === null
                  ? t('monthlyReport.clients.dsoHint')
                  : `${t('monthlyReport.summary.vsPrevious')} ${t('monthlyReport.clients.dsoValue', { n: clients.dsoPrevious })}`
              }
            />
            <Stat
              label={t('monthlyReport.clients.concentration')}
              value={clients.concentration ? fmtPct(clients.concentration.top1) : '—'}
              hint={
                clients.concentration
                  ? `${t('monthlyReport.clients.top3')}: ${fmtPct(clients.concentration.top3)}`
                  : undefined
              }
              tone={clients.concentration && clients.concentration.top1 >= 50 ? 'bad' : 'neutral'}
            />
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-3 font-medium">{t('monthlyReport.clients.client')}</th>
                  <th className="pb-2 pr-3 text-right font-medium">
                    {t('monthlyReport.clients.invoiced')}
                  </th>
                  <th className="pb-2 pr-3 text-right font-medium">
                    {t('monthlyReport.clients.collected')}
                  </th>
                  <th className="pb-2 pr-3 text-right font-medium">
                    {t('monthlyReport.clients.open')}
                  </th>
                  <th className="pb-2 pr-3 text-right font-medium">
                    {t('monthlyReport.clients.overdue')}
                  </th>
                  <th className="pb-2 pr-3 text-right font-medium">
                    {t('monthlyReport.clients.invoiceCount')}
                  </th>
                  <th className="pb-2 text-right font-medium">
                    {t('monthlyReport.clients.dsoShort')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((c) => (
                  <tr key={c.key}>
                    <td className="py-1.5 pr-3 text-gray-800">
                      {c.clientId ? (
                        <Link to={`/clients/${c.clientId}`} className="hover:underline">
                          {c.name}
                        </Link>
                      ) : (
                        c.name
                      )}
                      {c.isNew && (
                        <span className="ml-1.5 rounded border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-green-700">
                          {t('monthlyReport.clients.newBadge')}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">
                      {fmtMoney(c.invoiced, 'RON', locale)}
                      <span className="ml-2 text-xs text-gray-400">{fmtPct(c.share)}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                      {fmtMoney(c.collected, 'RON', locale)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                      {c.open > 0 ? fmtMoney(c.open, 'RON', locale) : '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {c.overdue > 0 ? (
                        <span className="font-medium text-red-700">
                          {fmtMoney(c.overdue, 'RON', locale)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                      {c.invoiceCount}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-600">
                      {c.dso === null ? '—' : c.dso}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {clients.rows.length > ROWS_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 text-sm font-medium text-brand-600 hover:underline"
            >
              {showAll
                ? t('monthlyReport.clients.showLess')
                : t('monthlyReport.clients.showAll', { n: clients.rows.length })}
            </button>
          )}

          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <PartyList
              title={t('monthlyReport.clients.overdueByClient')}
              rows={clients.overdueByClient}
            />
            {clients.dormant.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700">
                  {t('monthlyReport.clients.dormant')}
                </h3>
                <p className="text-xs text-gray-500">{t('monthlyReport.clients.dormantHint')}</p>
                <ul className="mt-2 divide-y divide-gray-100">
                  {clients.dormant.map((d) => (
                    <li key={d.key} className="py-1.5 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate text-gray-800">{d.name}</span>
                        <span className="shrink-0 tabular-nums text-gray-900">
                          {fmtMoney(d.lastAmount, 'RON', locale)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        {t('monthlyReport.clients.dormantRow', {
                          date: fmtDate(d.lastInvoiceAt, locale),
                          days: d.daysSince,
                        })}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {trend.some((p) => p.invoiced > 0 || p.collected > 0) && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700">
                {t('monthlyReport.clients.trend')}
              </h3>
              <div className="mt-2 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={trend}
                    margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                    barGap={2}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={AXIS}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={12}
                    />
                    <YAxis
                      tick={AXIS}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={compact}
                      width={44}
                    />
                    <Tooltip
                      contentStyle={CHART_TOOLTIP_STYLE}
                      labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                      formatter={(v) => fmtMoney(v as number, 'RON', locale)}
                      cursor={{ fill: '#F3F4F6' }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="invoiced"
                      name={t('monthlyReport.clients.invoiced')}
                      fill={REPORT_CHART_COLORS.net}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={14}
                    />
                    <Bar
                      dataKey="collected"
                      name={t('monthlyReport.clients.collected')}
                      fill={REPORT_CHART_COLORS.inflows}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={14}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {clients.otherCurrencies.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              {clients.otherCurrencies
                .map((c) => fmtMoney(c.amount, c.currency, locale))
                .join(' · ')}
            </p>
          )}
        </>
      )}
    </ReportSection>
  );
}
